import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DataTableComponent, TableColumn } from '@shared/components/data-table/data-table.component';
import { SynchronizationDetail } from '../../../models/synchronization.model';
import { MessageService } from 'primeng/api';
import { getAdjustmentTypeClass, getAdjustmentTypeText } from '../../../synchronization.config';

export interface ApprovalDetailsDialogConfig {
  title: string;
  width: string;
  maxWidth: string;
  height?: string;
  maxHeight?: string;
  tableColumns: TableColumn[];
}

@Component({
  selector: 'app-approval-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    DataTableComponent,
    TranslateModule
  ],
  templateUrl: './approval-details-dialog.component.html',
  styleUrls: ['./approval-details-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprovalDetailsDialogComponent {
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);
  private cdr = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Input() config: ApprovalDetailsDialogConfig | null = null;
  @Input() adjustmentDetails: SynchronizationDetail[] = [];
  @Input() loading = false;
  @Input() synchronizationData: any = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() approve = new EventEmitter<any>();
  @Output() reject = new EventEmitter<any>();

  // Validation state
  fieldValidationErrors = signal<Record<string, string>>({});

  // Computed properties
  dialogStyle = computed(() => ({
    width: this.config?.width,
    maxWidth: this.config?.maxWidth,
    height: this.config?.height,
    maxHeight: this.config?.maxHeight
  }));


  onVisibleChange(visible: boolean): void {
    // Clear validation errors when dialog is closed (X button or ESC)
    if (!visible) {
      this.fieldValidationErrors.set({});
      // Reset loading state when dialog is closed
      this.loading = false;
    }
    this.visibleChange.emit(visible);
  }

  onApprove(): void {
    // Prevent multiple clicks - check if already loading
    if (this.loading) {
      return;
    }
    
    // Validate before approval
    if (!this.validateBeforeApproval()) {
      return;
    }
    
    // Set loading state to prevent multiple clicks
    this.loading = true;
    
    // Prepare approval data with updated adjustment details
    const approvalData = {
      synchronizationData: {
        ...this.synchronizationData,
        adjustDetails: this.adjustmentDetails // Use the updated adjustment details with user input
      },
      adjustmentDetails: this.adjustmentDetails
    };

    this.approve.emit(approvalData);
  }

  private validateBeforeApproval(): boolean {
    const fieldErrors: Record<string, string> = {};

    // Validate individual adjustment details
    this.adjustmentDetails.forEach((detail, index) => {
      // Validate reality number - must equal plan number
      if (detail.realityNum === null || detail.realityNum === undefined) {
        fieldErrors[`${index}-realityNum`] = this.translateService.instant('synchronization.validation.reality_num_required');
      } else if (detail.planNum && detail.realityNum > detail.planNum) {
        fieldErrors[`${index}-realityNum`] = this.translateService.instant('synchronization.validation.reality_num_must_equal_plan_num');
      }
    });

    // // Set field validation errors
    this.fieldValidationErrors.set(fieldErrors);

    // // If there are field errors, don't approve
    if (Object.keys(fieldErrors).length > 0) {
      return false;
    }

    // Clear validation errors if validation passes
    this.fieldValidationErrors.set({});
    return true;
  }

  onReject(): void {
    // Prevent multiple clicks - check if already loading
    if (this.loading) {
      return;
    }
    
    // Set loading state to prevent multiple clicks
    this.loading = true;
    
    // Clear validation errors when rejecting
    this.fieldValidationErrors.set({});
    this.reject.emit(this.synchronizationData);
    // this.visibleChange.emit(false);
  }

  onCancel(): void {
    // Clear validation errors when canceling
    this.fieldValidationErrors.set({});
    // Reset loading state when canceling
    this.loading = false;
    this.visibleChange.emit(false);
  }

  getAdjustmentTypeText(type: string): string {
    const translationKey = getAdjustmentTypeText(type);
    return this.translateService.instant(translationKey);
  }

  getAdjustmentTypeClass(type: string): string {
    return getAdjustmentTypeClass(type);
  }

  // Method to get field validation error
  getFieldError(rowIndex: number, fieldName: string): string | null {
    const fieldKey = `${rowIndex}-${fieldName}`;
    return this.fieldValidationErrors()[fieldKey] || null;
  }

  // Method to handle cell value changes
  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
 
    // Update the adjustment details data
    const updatedDetails = this.adjustmentDetails.map(detail => {
      if (detail.id === event.rowData.id) {
        const updatedDetail = { ...detail, [event.prop]: event.value };
        return updatedDetail;
      }
      return detail;
    });
    
    // Update the input property (this will trigger change detection)
    this.adjustmentDetails = [...updatedDetails]; // Create new array reference

    // Clear field validation error for this specific field
    const detailIndex = this.adjustmentDetails.findIndex(d => d.id === event.rowData.id);
    if (detailIndex !== -1) {
      const fieldKey = `${detailIndex}-${event.prop}`;
      const currentErrors = this.fieldValidationErrors();
      if (currentErrors[fieldKey]) {
        const newErrors = { ...currentErrors };
        delete newErrors[fieldKey];
        this.fieldValidationErrors.set(newErrors);
      }
    }
  }

  // Method to reset loading state (called from parent when the approve/reject
  // API call fails). markForCheck is required because this component is
  // OnPush and this mutation happens from outside any of its own template
  // events, so it wouldn't otherwise be picked up on the next CD cycle.
  resetLoadingState(): void {
    this.loading = false;
    this.cdr.markForCheck();
  }
}
